import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const TermsAndConditionsModal = ({
  open,
  onClose,
  onSave,
  termsData = null,
  mode = 'create', // 'create', 'edit', 'view'
  existingTerms = [],
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
    IsType: 1,
    IsInternational: false,
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (termsData && mode !== 'create') {
        setFormData({
          Id: termsData.Id || '',
          Name: termsData.Name || '',
          IsType: termsData.IsType ?? 1,
          IsInternational: termsData.IsInternational ?? false,
        });
      } else {
        setFormData({ Name: '', IsType: 1, IsInternational: false });
      }
      setErrors({});
    }
  }, [open, termsData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name || !formData.Name.trim()) {
      newErrors.Name = 'Terms & Conditions text is required';
    }
    if (formData.IsType === '' || formData.IsType === null || formData.IsType === undefined) {
      newErrors.IsType = 'Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => {
    return existingTerms.find((item) => {
      if (mode === 'edit' && termsData && item.Id === termsData.Id) return false;
      const nameMatch =
        item.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();
      const typeMatch = item.IsType === Number(formData.IsType);
      return nameMatch && typeMatch;
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(
        `A Terms & Conditions entry with the same text and type already exists.`
      );
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        IsType: Number(formData.IsType),
        IsInternational: formData.IsInternational,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        if (!termsData || !termsData.Id) {
          alert('Error: Cannot update - missing ID');
          return;
        }
        dataToSend.Id = termsData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (termsData.CreatedBy) dataToSend.CreatedBy = termsData.CreatedBy;
        if (termsData.CreatedDate) dataToSend.CreatedDate = termsData.CreatedDate;
        await updateData('TermsAndConditionApi', termsData.Id, dataToSend, token);
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('TermsAndConditionApi', '', dataToSend, token);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Terms & Conditions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Terms & Conditions';
      case 'edit':   return 'Edit Terms & Conditions';
      case 'view':   return 'Terms & Conditions Details';
      default:       return 'Terms & Conditions';
    }
  };

  const isReadOnly = mode === 'view';

  const inputStyle = { borderRadius: '6px', fontSize: '13px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  const typeOptions = [
    { value: 1, label: 'Purchase', icon: '🛒', color: '#0f3460', hoverColor: '#16213e' },
    { value: 2, label: 'Sales',    icon: '💰', color: '#11998e', hoverColor: '#0d7a6f' },
    { value: 3, label: 'Both',     icon: '🔄', color: '#9b59b6', hoverColor: '#7d3c98' },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px',
          },
        }}
      >
        <Box
          style={{
            background: 'white',
            borderRadius: isMobile ? '0' : '10px',
            overflow: 'hidden',
            height: isMobile ? '100vh' : 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Title */}
          <DialogTitle
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              color: 'white',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 'auto',
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <GavelIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          {/* Content */}
          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px', flex: 1, overflowY: 'auto' }}>
            <Typography
              variant="subtitle2"
              style={{ color: '#666', marginBottom: '6px', fontSize: '10px', lineHeight: '1.2' }}
            >
              {mode === 'create'
                ? 'Add new terms & conditions to your system'
                : mode === 'edit'
                ? 'Update terms & conditions information'
                : 'View terms & conditions details'}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: '600', marginBottom: '3px', color: '#2d3748', fontSize: '12px' }}
                >
                  Terms & Conditions Information
                </Typography>
              </Grid>

              {/* Type - Button Group Selector */}
              <Grid item xs={12}>
                <Typography
                  style={{ fontSize: '11px', color: '#555', marginBottom: '5px', fontWeight: '500' }}
                >
                  Type <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box style={{ display: 'flex', gap: '8px' }}>
                  {typeOptions.map((opt) => {
                    const isSelected = Number(formData.IsType) === opt.value;
                    return (
                      <Box
                        key={opt.value}
                        onClick={() => {
                          if (!isReadOnly) {
                            handleChange({ target: { name: 'IsType', value: opt.value } });
                          }
                        }}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: isSelected
                            ? `2px solid ${opt.color}`
                            : '2px solid #e0e0e0',
                          background: isSelected
                            ? `linear-gradient(135deg, ${opt.color}18 0%, ${opt.color}30 100%)`
                            : '#fafafa',
                          cursor: isReadOnly ? 'default' : 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected
                            ? `0 2px 10px ${opt.color}40`
                            : '0 1px 3px rgba(0,0,0,0.06)',
                          transform: isSelected ? 'translateY(-1px)' : 'none',
                          opacity: isReadOnly && !isSelected ? 0.5 : 1,
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ fontSize: '20px', lineHeight: 1 }}>{opt.icon}</span>
                        <Typography
                          style={{
                            fontSize: '11px',
                            fontWeight: isSelected ? '700' : '500',
                            color: isSelected ? opt.color : '#666',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {opt.label}
                        </Typography>
                        {isSelected && (
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: opt.color,
                              marginTop: '2px',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {errors.IsType && (
                  <Typography style={{ fontSize: '9px', color: '#d32f2f', marginTop: '3px', marginLeft: '2px' }}>
                    {errors.IsType}
                  </Typography>
                )}
              </Grid>

              {/* Scope — Local / International */}
              <Grid item xs={12}>
                <Typography
                  style={{ fontSize: '11px', color: '#555', marginBottom: '5px', fontWeight: '500' }}
                >
                  Scope <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: false, label: 'Local',         icon: '🏠', color: '#0f3460', hoverColor: '#16213e' },
                    { value: true,  label: 'International', icon: '🌐', color: '#e67e22', hoverColor: '#d35400' },
                  ].map((opt) => {
                    const isSelected = formData.IsInternational === opt.value;
                    return (
                      <Box
                        key={String(opt.value)}
                        onClick={() => {
                          if (!isReadOnly) {
                            setFormData((prev) => ({ ...prev, IsInternational: opt.value }));
                          }
                        }}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '10px 8px',
                          borderRadius: '10px',
                          border: isSelected
                            ? `2px solid ${opt.color}`
                            : '2px solid #e0e0e0',
                          background: isSelected
                            ? `linear-gradient(135deg, ${opt.color}18 0%, ${opt.color}30 100%)`
                            : '#fafafa',
                          cursor: isReadOnly ? 'default' : 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected
                            ? `0 2px 10px ${opt.color}40`
                            : '0 1px 3px rgba(0,0,0,0.06)',
                          transform: isSelected ? 'translateY(-1px)' : 'none',
                          opacity: isReadOnly && !isSelected ? 0.5 : 1,
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ fontSize: '20px', lineHeight: 1 }}>{opt.icon}</span>
                        <Typography
                          style={{
                            fontSize: '11px',
                            fontWeight: isSelected ? '700' : '500',
                            color: isSelected ? opt.color : '#666',
                            letterSpacing: '0.3px',
                          }}
                        >
                          {opt.label}
                        </Typography>
                        {isSelected && (
                          <Box
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              background: opt.color,
                              marginTop: '2px',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* Terms Text (multiline) */}
              <Grid item xs={12}>
                <TextField
                  name="Name"
                  label="Terms & Conditions Text *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  multiline
                  rows={5}
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter terms & conditions text..."
                  InputProps={{ style: { ...inputStyle, height: 'auto' } }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          {/* Actions */}
          <DialogActions
            style={{
              padding: '8px 12px',
              background: '#f8f9fa',
              borderTop: '1px solid #e9ecef',
              gap: '6px',
              minHeight: 'auto',
            }}
          >
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px',
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px',
                }}
              >
                {loading
                  ? 'Saving...'
                  : mode === 'create'
                  ? 'Add Terms'
                  : 'Update Terms'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography
            variant="h6"
            style={{ fontWeight: '600', marginBottom: '12px', color: '#2d3748', fontSize: '16px' }}
          >
            Duplicate Entry Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TermsAndConditionsModal;
